import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Mail, ArrowRight, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { GooseLogo, Spinner, FadeIn } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";

function CountUp({ target }) {
  const [v, setV] = useState(0);
  useEffect(() => {
    let raf; const start = performance.now(); const dur = 900;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / dur);
      setV(Math.round((1 - Math.pow(1 - p, 3)) * target));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target]);
  return <>{v}</>;
}

export default function GuestNight() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [previewCode, setPreviewCode] = useState(null);
  const [session, setSession] = useState(null);
  const [badge, setBadge] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const { data: event, isLoading, error } = useQuery({
    queryKey: ["pub-event-night", slug],
    queryFn: async () => (await api.get(`/public/events/${slug}`)).data,
  });

  // Keep the guest authenticated for the whole night (no re-code per lookup).
  useEffect(() => {
    const saved = localStorage.getItem(`goose_guest_${slug}`);
    if (saved) {
      try { setSession(JSON.parse(saved)); setStep("portal"); } catch { /* ignore */ }
    }
  }, [slug]);

  if (isLoading) return <div className="goose-grain min-h-screen"><Spinner /></div>;
  if (error) return (
    <div className="flex min-h-screen items-center justify-center goose-grain px-6 text-center">
      <div><GooseLogo className="text-3xl" /><p className="mt-4 text-slate-400">This event link is not valid.</p></div>
    </div>
  );

  const notReady = !["goose_ready", "frozen", "billed", "payment_due"].includes(event.status);

  const requestCode = async () => {
    if (!email) { toast.error("Enter your email"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/public/events/${slug}/request-code`, { email });
      if (!data.found) { toast.error(data.message); }
      else {
        setPreviewCode(data.preview_code || null);
        toast.success("Code sent — check your email");
        setStep("code");
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send code"); }
    finally { setBusy(false); }
  };

  const verify = async (value) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/public/events/${slug}/verify`, { email, code: value });
      setSession(data);
      localStorage.setItem(`goose_guest_${slug}`, JSON.stringify(data));
      setStep("portal");
    } catch (e) { toast.error(e.response?.data?.detail || "Invalid code"); setCode(""); }
    finally { setBusy(false); }
  };

  const signOut = () => {
    localStorage.removeItem(`goose_guest_${slug}`);
    setSession(null); setResult(null); setBadge(""); setCode(""); setEmail(""); setStep("email");
  };

  const lookup = async () => {
    if (!badge) return;
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post(`/public/events/${slug}/lookup`, { guest_token: session.guest_token, badge_number: Number(badge) });
      setResult(data);
    } catch (e) {
      if (e.response?.status === 401) { toast.error("Your session expired — please verify again"); signOut(); }
      else { toast.error(e.response?.data?.detail || "Lookup failed"); }
    }
    finally { setBusy(false); }
  };

  return (
    <div className="goose-grain min-h-screen">
      <div className="relative z-10 mx-auto flex min-h-screen w-full max-w-md flex-col px-4 py-8">
        <GooseLogo className="text-xl" onClick={() => navigate("/")} />
        <p className="mt-2 text-xs font-semibold uppercase tracking-widest text-rose-400">{event.name}</p>

        <div className="mt-8 flex-1">
          <AnimatePresence mode="wait">
            {notReady ? (
              <FadeIn key="notready">
                <div className="goose-card p-8 text-center">
                  <Sparkles className="mx-auto h-8 w-8 text-amber-400" />
                  <h2 className="font-display mt-4 text-xl font-bold">Not open yet</h2>
                  <p className="mt-2 text-sm text-slate-400">The Goose experience unlocks one hour before the event. Come back then!</p>
                </div>
              </FadeIn>
            ) : step === "email" ? (
              <FadeIn key="email">
                <h1 className="font-display text-3xl font-bold">Unlock your experience</h1>
                <p className="mt-2 text-sm text-slate-400">Enter the email you used with HAEVN. We'll send you a 6-digit code.</p>
                <div className="goose-card mt-6 p-6">
                  <Label className="mb-1.5 block text-xs text-slate-400">Email</Label>
                  <div className="relative">
                    <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                    <Input data-testid="night-email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" className="pl-9" />
                  </div>
                  <Button data-testid="night-sendcode-btn" disabled={busy} onClick={requestCode} className="btn-pill mt-5 w-full bg-rose-500 py-6 font-bold hover:bg-rose-400">
                    {busy ? "Sending…" : "Send my code"} <ArrowRight className="ml-1.5 h-4 w-4" />
                  </Button>
                </div>
              </FadeIn>
            ) : step === "code" ? (
              <FadeIn key="code">
                <h1 className="font-display text-3xl font-bold">Enter your code</h1>
                <p className="mt-2 text-sm text-slate-400">Sent to {email}</p>
                {previewCode && (
                  <div data-testid="preview-code-banner" className="mt-3 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-center">
                    <p className="text-[11px] font-semibold uppercase tracking-widest text-amber-300">Preview / test mode</p>
                    <p className="font-mono-g mt-1 text-2xl font-black tracking-[0.4em] text-amber-300" data-testid="preview-code-value">{previewCode}</p>
                    <p className="mt-1 text-[11px] text-amber-200/70">Shown only in preview. Production is email-only.</p>
                  </div>
                )}
                <div className="goose-card mt-6 flex flex-col items-center p-6">
                  <InputOTP maxLength={6} value={code} onChange={(v) => { setCode(v); if (v.length === 6) verify(v); }} data-testid="night-otp-input">
                    <InputOTPGroup>
                      {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className="h-12 w-11 border-slate-700 text-xl" />)}
                    </InputOTPGroup>
                  </InputOTP>
                  <button onClick={() => { setStep("email"); setCode(""); }} className="mt-5 text-xs text-slate-500 hover:text-slate-300">Use a different email</button>
                </div>
              </FadeIn>
            ) : (
              <FadeIn key="portal">
                <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: "spring", stiffness: 220, damping: 16 }}
                  className="goose-card relative overflow-hidden p-7 text-center" data-testid="my-badge">
                  <div className="absolute -inset-10 bg-rose-500/20 blur-3xl" />
                  <div className="relative">
                    <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">You're</p>
                    <p className="font-mono-g mt-2 text-6xl font-black tracking-widest text-rose-500 glow-rose" data-testid="my-badge-number">#{session.goose_number}</p>
                    <p className="mt-2 text-sm text-slate-300">{session.name}</p>
                    <p className="mt-1 text-xs text-slate-500">This should match the number on your physical badge.</p>
                  </div>
                </motion.div>

                <div className="goose-card mt-5 p-6">
                  <p className="font-display text-lg font-semibold">Who caught your attention?</p>
                  <p className="text-sm text-slate-400">Enter their Goose number to see how well you align.</p>
                  <div className="mt-4 flex gap-2">
                    <div className="relative flex-1">
                      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 font-mono-g text-lg text-slate-500">#</span>
                      <Input data-testid="lookup-badge-input" inputMode="numeric" value={badge}
                        onChange={(e) => setBadge(e.target.value.replace(/\D/g, ""))}
                        onKeyDown={(e) => e.key === "Enter" && lookup()}
                        placeholder="e.g. 7" className="pl-8 font-mono-g text-lg" />
                    </div>
                    <Button data-testid="lookup-btn" disabled={busy} onClick={lookup} className="btn-pill bg-rose-500 px-5 font-bold hover:bg-rose-400">Check compatibility</Button>
                  </div>

                  <AnimatePresence mode="wait">
                    {result && (
                      <motion.div key={result.status + (result.their_number || "")} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                        className="mt-5" data-testid="lookup-result">
                        {result.status === "ok" ? (
                          <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-6 text-center">
                            <p className="font-mono-g text-xs text-slate-400">#{result.your_number} × #{result.their_number}</p>
                            {result.their_photo
                              ? <img src={result.their_photo} alt="" className="mx-auto mt-3 h-16 w-16 rounded-full object-cover ring-2 ring-amber-500/40" />
                              : <div className="mx-auto mt-3 flex h-16 w-16 items-center justify-center rounded-full bg-slate-800 ring-2 ring-slate-700"><span className="font-mono-g text-lg font-bold text-slate-500">#{result.their_number}</span></div>}
                            <p className="font-mono-g mt-3 text-7xl font-black text-amber-400 glow-amber"><CountUp target={result.compatibility_pct} />%</p>
                            {result.headline && <p className="mt-2 text-sm font-semibold text-amber-200" data-testid="lookup-headline">{result.headline}</p>}
                            {result.considerations && <p className="mt-2 text-sm text-slate-300" data-testid="lookup-considerations">{result.considerations}</p>}
                          </div>
                        ) : (
                          <div className="rounded-2xl border border-slate-800 bg-black/20 p-5 text-center text-sm text-slate-400" data-testid="lookup-message">
                            {result.message}
                          </div>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                  {result && result.status === "ok" && (
                    <button data-testid="check-another-btn" onClick={() => { setBadge(""); setResult(null); }}
                      className="mt-4 w-full text-center text-xs font-semibold text-rose-400 hover:text-rose-300">Check another Goose number</button>
                  )}
                </div>

                <button data-testid="night-signout-btn" onClick={signOut}
                  className="mt-4 w-full text-center text-xs text-slate-500 hover:text-slate-300">Not you? Use a different email</button>
              </FadeIn>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
